import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "../../components/states";
import { NumberField, SettingForm, SettingProblem, SettingSubsection } from "../../components/admin/SettingPanel";
import { useToast } from "../../components/toast/toastContext";
import { fetchSessionLimits, updateSessionLimits, type SessionLimits } from "../../api/sessionLimits";

const SettingsWritePermission = "settings.write";

/** The longest absolute limit, in hours: 14 days. Mirrors `SessionLimits.MaximumAbsoluteHours` on the server, which refuses anything above it. */
export const MaximumAbsoluteHours = 336;

interface Draft {
  absoluteHours: string;
  idleHours: string;
}

function toDraft(limits: SessionLimits): Draft {
  return { absoluteHours: String(limits.absoluteHours), idleHours: String(limits.idleHours) };
}

/** A whole number of hours, as the text an administrator typed. Anything else, including an empty box, is not a value. */
function parseWhole(text: string): number | null {
  return /^\d+$/.test(text.trim()) ? Number(text.trim()) : null;
}

/**
 * The checks the server makes, applied as the administrator types so an out-of-range value cannot be saved: the absolute limit is a whole
 * number of hours from 1 to 336, and the idle limit a whole number from 1 up to the absolute limit.
 */
function validate(draft: Draft): { absolute: number | null; idle: number | null; absoluteValid: boolean; idleValid: boolean } {
  const absolute = parseWhole(draft.absoluteHours);
  const idle = parseWhole(draft.idleHours);
  const absoluteValid = absolute !== null && absolute >= 1 && absolute <= MaximumAbsoluteHours;
  const idleValid = idle !== null && idle >= 1 && (!absoluteValid || idle <= (absolute ?? 0));
  return { absolute, idle, absoluteValid, idleValid };
}

/**
 * How long a signed-in portal session lasts: the absolute limit from sign-in, and the idle limit after which an unused session ends.
 * Saving applies to every session at its next request. The fields refuse a value outside the limits before it is sent, and the server
 * refuses it again if it arrives anyway.
 */
export function SessionLimitsSection(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded" } | { kind: "error"; message: string }>({ kind: "loading" });
  const [draft, setDraft] = useState<Draft>({ absoluteHours: "", idleHours: "" });
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchSessionLimits();
    if (result.kind === "ok") {
      setDraft(toDraft(result.response));
      setState({ kind: "loaded" });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.sessionLimits.denied") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const { absolute, idle, absoluteValid, idleValid } = validate(draft);
  const canSave = absoluteValid && idleValid && !pending;

  const save = async () => {
    if (absolute === null || idle === null || !canSave) {
      return;
    }

    setPending(true);
    const result = await updateSessionLimits(absolute, idle);
    setPending(false);
    if (result.kind === "ok") {
      setDraft(toDraft(result.response));
      toast.show(t("admin.sessionLimits.saved"), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  return (
    <SettingSubsection id="session-limits" title={t("admin.sessionLimits.title")} explanation={t("admin.sessionLimits.explanation", { maximum: MaximumAbsoluteHours })}>
      {state.kind === "loading" && <LoadingState label={t("admin.sessionLimits.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" && (
        <>
          <SettingForm>
            <NumberField
              id="session-absolute-hours"
              label={t("admin.sessionLimits.absoluteHours")}
              min={1}
              max={MaximumAbsoluteHours}
              value={draft.absoluteHours}
              invalid={!absoluteValid}
              describedBy="session-limits-validation"
              onChange={(absoluteHours) => setDraft({ ...draft, absoluteHours })}
            />
            <NumberField
              id="session-idle-hours"
              label={t("admin.sessionLimits.idleHours")}
              min={1}
              max={absoluteValid ? absolute ?? undefined : undefined}
              value={draft.idleHours}
              invalid={!idleValid}
              describedBy="session-limits-validation"
              onChange={(idleHours) => setDraft({ ...draft, idleHours })}
            />
            <button type="button" className="button button-secondary" disabled={!canSave} onClick={() => void save()}>
              {t("admin.sessionLimits.save")}
            </button>
          </SettingForm>
          {!absoluteValid || !idleValid ? (
            <SettingProblem id="session-limits-validation">
              {absoluteValid ? t("admin.sessionLimits.idleError", { maximum: absolute }) : t("admin.sessionLimits.absoluteError", { maximum: MaximumAbsoluteHours })}
            </SettingProblem>
          ) : null}
        </>
      )}
    </SettingSubsection>
  );
}
